import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'
import { ActionButton } from '@react-spectrum/s2'
import {
  AlertCircle,
  BookOpen,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  ExternalLink,
  RotateCcw,
} from 'lucide-react'
import {
  Children,
  isValidElement,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  extractDiagramMarkers,
  extractKeyPhraseMarkers,
  extractTableMarkers,
  stripCitationMarkersWithMap,
} from '@shared/citation-markers'
import { normalizeGroundingSupportsForDisplay } from '@shared/grounding-integrity'
import { trackAnalyticsEvent } from '@client/analytics'
import { MessageFeedbackControl } from '@/components/MessageFeedbackControl'
import type { MessageFeedbackSummary } from '@shared/chat-types'
import { cn } from '@/lib/utils'

const markdownSanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    ...defaultSchema.tagNames!.filter((tagName) => tagName !== 'img'),
    'diagram-inline',
    'keyphrase-card',
    'table-inline',
  ],
  attributes: {
    ...defaultSchema.attributes,
    span: [
      ...((defaultSchema.attributes?.span as Array<string> | undefined) ?? []),
      'className',
      'class',
      'data-chunks',
      'dataChunks',
      'data-source-reference-id',
      'dataSourceReferenceId',
    ],
    'diagram-inline': ['data-diagram-id', 'dataDiagramId'],
    'keyphrase-card': [
      'data-keyphrase',
      'dataKeyphrase',
      'data-chunks',
      'dataChunks',
    ],
    'table-inline': ['data-table-id', 'dataTableId'],
  },
}

const CHAT_TIMESTAMP_LOCALE = 'en-GB'
const CHAT_TIMESTAMP_TIME_ZONE = 'Europe/London'
const USER_MESSAGE_COLLAPSE_MIN_CHARACTERS = 280
const USER_MESSAGE_COLLAPSE_MIN_LINES = 8
const SOURCE_SEGMENT_CLASS =
  'citation-segment inline cursor-pointer rounded-sm px-0.5 text-neutral-950 underline decoration-neutral-400/80 decoration-dotted underline-offset-4 transition-colors hover:bg-neutral-100/80 hover:decoration-neutral-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-700 dark:text-neutral-50 dark:decoration-neutral-500/80 dark:hover:bg-neutral-800/80 dark:hover:decoration-neutral-200 dark:focus-visible:outline-neutral-300'
const SOURCE_REFERENCE_SEGMENT_CLASS =
  'ring-1 ring-inset ring-neutral-300/70 dark:ring-neutral-700/80'
const CHAT_TIMESTAMP_FORMATTER = new Intl.DateTimeFormat(
  CHAT_TIMESTAMP_LOCALE,
  {
    timeZone: CHAT_TIMESTAMP_TIME_ZONE,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  },
)

interface GroundingSupport {
  segment: {
    startIndex: number
    endIndex: number
    text: string
  }
  groundingChunkIndices: Array<number>
}

interface ChatMessageProps {
  messageId?: string
  role: 'user' | 'assistant'
  content: string
  createdAt?: string | Date | null
  rawContent?: string | null
  onCitationClick?: (chunkIndices: Array<number>) => void
  onOpenSourcePdf?: (citation: {
    title: string
    pageStart?: number
    pageEnd?: number
    pageImageUrl?: string | null
    sourcePdfUrl?: string | null
  }) => void
  groundingSupports?: Array<GroundingSupport>
  citations?: Array<any>
  visualEvidence?: Array<any>
  diagramRequests?: Array<any>
  tableRequests?: Array<any>
  sourceReferenceRequests?: Array<any>
  verificationStatus?: 'verifying' | 'verified' | 'unverified'
  onDiagramClick?: (diagramRequest: any) => void
  onOpenSourceReference?: (sourceReferenceRequest: any) => void
  onSourceEvidenceOpen?: () => void
  sourcesVisible?: boolean
  feedback?: MessageFeedbackSummary | null
  previousUserMessage?: string | null
  conversationUrl?: string | null
  onRetry?: () => void
}

type NormalizedGroundingSupport = {
  segment: {
    startIndex: number
    endIndex: number
    text: string
  }
  groundingChunkIndices: Array<number>
}

type SpecialInsertion = {
  cleanIndex: number
  kind: 'keyphrase'
  text: string
  chunkIndices?: Array<number>
}

type SourceReferenceInsertion = {
  cleanIndex: number
  cleanEndIndex: number
  sourceReferenceId: string
  occurrenceIndex: number
}

type BlockVisualInsertion =
  | {
      cleanIndex: number
      kind: 'diagram'
      diagramId: string
      occurrenceIndex: number
    }
  | {
      cleanIndex: number
      kind: 'table'
      tableId: string
      occurrenceIndex: number
    }

function formatMessageTimestamp(value: string | Date | null | undefined) {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return null

  return CHAT_TIMESTAMP_FORMATTER.format(date)
}

function collectCitationChunkIndices(node: unknown): Array<number> {
  const collected = new Set<number>()

  const visit = (value: unknown) => {
    if (Array.isArray(value)) {
      value.forEach(visit)
      return
    }

    if (!isValidElement(value)) return

    const chunkIndicesStr = value.props['data-chunks']

    if (typeof chunkIndicesStr === 'string') {
      try {
        const chunkIndices = JSON.parse(chunkIndicesStr)
        if (Array.isArray(chunkIndices)) {
          chunkIndices.forEach((index) => {
            if (typeof index === 'number' && Number.isFinite(index)) {
              collected.add(index)
            }
          })
        }
      } catch {
        // Ignore malformed embedded chunk metadata
      }
    }

    if (value.props?.children) {
      visit(value.props.children)
    }
  }

  visit(node)
  return [...collected]
}

function isStandaloneRuleNode(node: unknown) {
  if (!isValidElement(node)) return false
  if (node.type === 'strong' || node.type === MarkdownStrong) return true

  const chunkIndicesStr = node.props['data-chunks']

  if (typeof chunkIndicesStr !== 'string') return false

  const nestedChildren = Children.toArray(node.props?.children).filter(
    (child) => {
      return !(typeof child === 'string' && child.trim().length === 0)
    },
  )

  return (
    nestedChildren.length === 1 &&
    isValidElement(nestedChildren[0]) &&
    (nestedChildren[0].type === 'strong' ||
      nestedChildren[0].type === MarkdownStrong)
  )
}

function MarkdownStrong({ children }: { children: React.ReactNode }) {
  return <strong className="font-medium text-inherit">{children}</strong>
}

function getNodeOffset(
  node: {
    position?: { start?: { offset?: number }; end?: { offset?: number } }
  },
  side: 'start' | 'end',
) {
  const offset =
    side === 'start' ? node.position?.start?.offset : node.position?.end?.offset
  return typeof offset === 'number' && Number.isFinite(offset) ? offset : null
}

function escapeHtml(text: string) {
  return text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function buildSpecialInsertionHtml(insertion: SpecialInsertion) {
  const encodedText = encodeURIComponent(insertion.text)
  const encodedChunks =
    insertion.chunkIndices && insertion.chunkIndices.length > 0
      ? encodeURIComponent(JSON.stringify(insertion.chunkIndices))
      : null

  return `<keyphrase-card data-keyphrase="${encodedText}"${encodedChunks ? ` data-chunks="${encodedChunks}"` : ''}></keyphrase-card>`
}

function escapeRegExp(text: string) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function buildSourceReferencePattern(request: any) {
  const kind =
    typeof request?.referenceKind === 'string' ? request.referenceKind : null
  const normalizedReference =
    typeof request?.normalizedReference === 'string'
      ? request.normalizedReference.trim()
      : ''

  if (!kind || !normalizedReference) {
    const fallbackText =
      typeof request?.referenceText === 'string'
        ? request.referenceText.trim()
        : ''
    return fallbackText ? new RegExp(escapeRegExp(fallbackText), 'i') : null
  }

  if (kind === 'appendix') {
    return new RegExp(
      `\\bAppendix\\s+${escapeRegExp(normalizedReference)}\\b`,
      'i',
    )
  }

  if (kind === 'regulation') {
    return new RegExp(
      `\\bRegulation\\s+${escapeRegExp(normalizedReference)}\\b`,
      'i',
    )
  }

  if (kind === 'section') {
    return new RegExp(
      `\\bSection\\s+${escapeRegExp(normalizedReference)}\\b`,
      'i',
    )
  }

  if (kind === 'paragraph') {
    const paragraphMatch = normalizedReference.match(/^(\d+\.\d+)([a-z])$/i)
    if (paragraphMatch) {
      const [, baseReference, suffix] = paragraphMatch
      return new RegExp(
        `\\bparagraph\\s+${escapeRegExp(baseReference)}(?:${escapeRegExp(
          suffix,
        )}\\b|\\(${escapeRegExp(suffix)}\\))`,
        'i',
      )
    }
    return new RegExp(
      `\\bparagraph\\s+${escapeRegExp(normalizedReference)}\\b`,
      'i',
    )
  }

  const fallbackText =
    typeof request?.referenceText === 'string'
      ? request.referenceText.trim()
      : ''
  return fallbackText ? new RegExp(escapeRegExp(fallbackText), 'i') : null
}

function locateSourceReferenceInsertion(
  text: string,
  request: any,
): SourceReferenceInsertion | null {
  if (
    typeof request?.id !== 'string' ||
    typeof request?.occurrenceIndex !== 'number'
  ) {
    return null
  }

  const pattern = buildSourceReferencePattern(request)
  if (pattern) {
    const match = pattern.exec(text)
    if (match && typeof match.index === 'number') {
      return {
        cleanIndex: match.index,
        cleanEndIndex: match.index + match[0].length,
        sourceReferenceId: request.id,
        occurrenceIndex: request.occurrenceIndex,
      }
    }
  }

  const referenceText =
    typeof request?.referenceText === 'string'
      ? request.referenceText.trim()
      : ''
  if (!referenceText) return null

  const fallbackIndex = text.toLowerCase().indexOf(referenceText.toLowerCase())
  if (fallbackIndex < 0) return null

  return {
    cleanIndex: fallbackIndex,
    cleanEndIndex: fallbackIndex + referenceText.length,
    sourceReferenceId: request.id,
    occurrenceIndex: request.occurrenceIndex,
  }
}

function findActiveSupport(
  supports: Array<NormalizedGroundingSupport>,
  startIndex: number,
  endIndex: number,
) {
  return supports.find(
    (support) =>
      startIndex >= support.segment.startIndex &&
      endIndex <= support.segment.endIndex,
  )
}

function findActiveSourceReference(
  insertions: Array<SourceReferenceInsertion>,
  startIndex: number,
  endIndex: number,
) {
  return insertions.find(
    (insertion) =>
      startIndex >= insertion.cleanIndex && endIndex <= insertion.cleanEndIndex,
  )
}

function splitTextNodeWithDecorations(
  node: any,
  normalizedSupports: Array<NormalizedGroundingSupport>,
  specialInsertions: Array<SpecialInsertion>,
  sourceReferenceInsertions: Array<SourceReferenceInsertion>,
  insertionIndex: number,
) {
  const nodeStart = getNodeOffset(node, 'start')
  const nodeEnd = getNodeOffset(node, 'end')

  if (
    node.type !== 'text' ||
    typeof node.value !== 'string' ||
    nodeStart === null ||
    nodeEnd === null
  ) {
    return { nodes: [node], nextInsertionIndex: insertionIndex }
  }

  let nextInsertionIndex = insertionIndex
  const boundaries = new Set<number>([nodeStart, nodeEnd])

  for (const support of normalizedSupports) {
    if (
      support.segment.endIndex <= nodeStart ||
      support.segment.startIndex >= nodeEnd
    ) {
      continue
    }
    boundaries.add(Math.max(nodeStart, support.segment.startIndex))
    boundaries.add(Math.min(nodeEnd, support.segment.endIndex))
  }

  while (
    nextInsertionIndex < specialInsertions.length &&
    specialInsertions[nextInsertionIndex].cleanIndex < nodeStart
  ) {
    nextInsertionIndex += 1
  }

  let scanIndex = nextInsertionIndex
  while (
    scanIndex < specialInsertions.length &&
    specialInsertions[scanIndex].cleanIndex <= nodeEnd
  ) {
    boundaries.add(specialInsertions[scanIndex].cleanIndex)
    scanIndex += 1
  }

  for (const insertion of sourceReferenceInsertions) {
    if (
      insertion.cleanEndIndex <= nodeStart ||
      insertion.cleanIndex >= nodeEnd
    ) {
      continue
    }
    boundaries.add(Math.max(nodeStart, insertion.cleanIndex))
    boundaries.add(Math.min(nodeEnd, insertion.cleanEndIndex))
  }

  const sortedBoundaries = [...boundaries].sort((left, right) => left - right)
  const nodes: Array<any> = []

  for (let index = 0; index < sortedBoundaries.length; index += 1) {
    const boundary = sortedBoundaries[index]

    while (
      nextInsertionIndex < specialInsertions.length &&
      specialInsertions[nextInsertionIndex].cleanIndex === boundary
    ) {
      nodes.push({
        type: 'html',
        value: buildSpecialInsertionHtml(specialInsertions[nextInsertionIndex]),
      })
      nextInsertionIndex += 1
    }

    const nextBoundary = sortedBoundaries[index + 1]
    if (typeof nextBoundary !== 'number' || nextBoundary <= boundary) {
      continue
    }

    const localStart = boundary - nodeStart
    const localEnd = nextBoundary - nodeStart
    const textSlice = node.value.slice(localStart, localEnd)
    if (!textSlice) continue

    const activeSupport = findActiveSupport(
      normalizedSupports,
      boundary,
      nextBoundary,
    )
    const activeSourceReference = findActiveSourceReference(
      sourceReferenceInsertions,
      boundary,
      nextBoundary,
    )

    if (activeSupport || activeSourceReference) {
      const attributes: Array<string> = []
      if (activeSupport) {
        attributes.push(
          `class="citation-segment" data-chunks="${escapeHtml(
            JSON.stringify(activeSupport.groundingChunkIndices),
          )}"`,
        )
      }
      if (activeSourceReference) {
        attributes.push(
          `data-source-reference-id="${escapeHtml(
            activeSourceReference.sourceReferenceId,
          )}"`,
        )
      }
      nodes.push({
        type: 'html',
        value: `<span ${attributes.join(' ')}>${escapeHtml(textSlice)}</span>`,
      })
      continue
    }

    nodes.push({
      ...node,
      value: textSlice,
    })
  }

  return { nodes, nextInsertionIndex }
}

function decorateMarkdownTree(
  node: any,
  normalizedSupports: Array<NormalizedGroundingSupport>,
  specialInsertions: Array<SpecialInsertion>,
  sourceReferenceInsertions: Array<SourceReferenceInsertion>,
  insertionState: { index: number },
) {
  if (!Array.isArray(node?.children)) return

  const decoratedChildren: Array<any> = []

  for (const child of node.children) {
    const childStart = getNodeOffset(child, 'start')

    while (
      childStart !== null &&
      insertionState.index < specialInsertions.length &&
      specialInsertions[insertionState.index].cleanIndex < childStart
    ) {
      decoratedChildren.push({
        type: 'html',
        value: buildSpecialInsertionHtml(
          specialInsertions[insertionState.index],
        ),
      })
      insertionState.index += 1
    }

    if (child?.type === 'text') {
      const { nodes, nextInsertionIndex } = splitTextNodeWithDecorations(
        child,
        normalizedSupports,
        specialInsertions,
        sourceReferenceInsertions,
        insertionState.index,
      )
      decoratedChildren.push(...nodes)
      insertionState.index = nextInsertionIndex
      continue
    }

    decorateMarkdownTree(
      child,
      normalizedSupports,
      specialInsertions,
      sourceReferenceInsertions,
      insertionState,
    )
    decoratedChildren.push(child)
  }

  const nodeEnd = getNodeOffset(node, 'end')
  while (
    nodeEnd !== null &&
    insertionState.index < specialInsertions.length &&
    specialInsertions[insertionState.index].cleanIndex <= nodeEnd
  ) {
    decoratedChildren.push({
      type: 'html',
      value: buildSpecialInsertionHtml(specialInsertions[insertionState.index]),
    })
    insertionState.index += 1
  }

  if (node?.type === 'root') {
    while (insertionState.index < specialInsertions.length) {
      decoratedChildren.push({
        type: 'html',
        value: buildSpecialInsertionHtml(
          specialInsertions[insertionState.index],
        ),
      })
      insertionState.index += 1
    }
  }

  node.children = decoratedChildren
}

function createChatDecorationsRemarkPlugin(
  normalizedSupports: Array<NormalizedGroundingSupport>,
  specialInsertions: Array<SpecialInsertion>,
  sourceReferenceInsertions: Array<SourceReferenceInsertion>,
) {
  return () => (tree: any) => {
    const insertionState = { index: 0 }
    decorateMarkdownTree(
      tree,
      normalizedSupports,
      specialInsertions,
      sourceReferenceInsertions,
      insertionState,
    )
  }
}

function buildTableInsertionHtml(tableId: string) {
  return `<table-inline data-table-id="${escapeHtml(tableId)}"></table-inline>`
}

function buildDiagramInsertionHtml(diagramId: string) {
  return `<diagram-inline data-diagram-id="${escapeHtml(diagramId)}"></diagram-inline>`
}

function buildBlockVisualInsertionHtml(insertion: BlockVisualInsertion) {
  return insertion.kind === 'diagram'
    ? buildDiagramInsertionHtml(insertion.diagramId)
    : buildTableInsertionHtml(insertion.tableId)
}

function compareInsertionOrder(
  left: { cleanIndex: number; occurrenceIndex?: number },
  right: { cleanIndex: number; occurrenceIndex?: number },
) {
  if (left.cleanIndex !== right.cleanIndex) {
    return left.cleanIndex - right.cleanIndex
  }
  return (left.occurrenceIndex ?? 0) - (right.occurrenceIndex ?? 0)
}

function nodeContainsCleanIndex(node: any, cleanIndex: number) {
  const start = getNodeOffset(node, 'start')
  const end = getNodeOffset(node, 'end')
  if (start == null || end == null) return false
  return cleanIndex >= start && cleanIndex <= end
}

function appendBlockVisualsToListItem(
  listItem: any,
  pendingInsertions: Array<BlockVisualInsertion>,
) {
  if (!Array.isArray(listItem?.children) || pendingInsertions.length === 0)
    return listItem
  const itemStart = getNodeOffset(listItem, 'start')
  const itemEnd = getNodeOffset(listItem, 'end')
  if (itemStart == null || itemEnd == null) return listItem

  const insertionsForItem: Array<BlockVisualInsertion> = []
  while (
    pendingInsertions.length > 0 &&
    pendingInsertions[0].cleanIndex >= itemStart &&
    pendingInsertions[0].cleanIndex <= itemEnd
  ) {
    insertionsForItem.push(pendingInsertions.shift() as BlockVisualInsertion)
  }

  if (insertionsForItem.length === 0) return listItem

  listItem.children = [
    ...listItem.children,
    ...insertionsForItem.map((insertion) => ({
      type: 'html',
      value: buildBlockVisualInsertionHtml(insertion),
    })),
  ]
  return listItem
}

function decorateBlockVisualInsertions(
  node: any,
  pendingInsertions: Array<BlockVisualInsertion>,
) {
  if (!Array.isArray(node?.children) || pendingInsertions.length === 0) return

  const nextChildren: Array<any> = []

  for (const child of node.children) {
    if (pendingInsertions.length === 0) {
      nextChildren.push(child)
      continue
    }

    if (child?.type === 'paragraph') {
      nextChildren.push(child)
      while (
        pendingInsertions.length > 0 &&
        nodeContainsCleanIndex(child, pendingInsertions[0].cleanIndex)
      ) {
        nextChildren.push({
          type: 'html',
          value: buildBlockVisualInsertionHtml(
            pendingInsertions.shift() as BlockVisualInsertion,
          ),
        })
      }
      continue
    }

    if (child?.type === 'list') {
      const listChildren = Array.isArray(child.children)
        ? [...child.children]
        : []
      child.children = listChildren.map((listItem: any) =>
        listItem?.type === 'listItem'
          ? appendBlockVisualsToListItem(listItem, pendingInsertions)
          : listItem,
      )
      nextChildren.push(child)
      continue
    }

    decorateBlockVisualInsertions(child, pendingInsertions)
    nextChildren.push(child)
  }

  node.children = nextChildren
}

function createBlockVisualInsertionRemarkPlugin(
  insertions: Array<BlockVisualInsertion>,
) {
  return () => (tree: any) => {
    const pendingInsertions = [...insertions].sort(
      (left, right) => left.occurrenceIndex - right.occurrenceIndex,
    )
    decorateBlockVisualInsertions(tree, pendingInsertions)
  }
}

function readDataAttribute(props: Record<string, any>, kebabName: string) {
  const camelName = kebabName.replace(/-([a-z])/g, (_match, char: string) =>
    char.toUpperCase(),
  )
  return props[kebabName] ?? props[camelName]
}

function normalizeTableHeadingText(text: string) {
  return text
    .replace(/[*_`#]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/\s+([:;,.])/g, '$1')
    .trim()
    .toLowerCase()
}

function stripInlineMarkdown(text: string) {
  return text
    .replace(/[*_`]/g, '')
    .replace(/\baboveground\b/gi, 'above ground')
    .replace(/\bof(?=perimeter\b)/gi, 'of ')
    .replace(/\s+/g, ' ')
    .trim()
}

const TABLE_BR_TOKEN = '__BUILDINGREGS_TABLE_BR__'
const INLINE_TABLE_PREVIEW_MAX_COLUMNS = 4
const INLINE_TABLE_PREVIEW_MAX_ROWS = 3

function parseMarkdownTableRow(line: string) {
  const trimmed = line.trim()
  if (!trimmed.includes('|')) return null
  const withoutEdges = trimmed.replace(/^\|/, '').replace(/\|$/, '')
  return withoutEdges.split('|').map((cell) => cell.trim())
}

function isMarkdownTableSeparator(line: string) {
  const cells = parseMarkdownTableRow(line)
  if (!cells || cells.length === 0) return false
  return cells.every((cell) => /^:?-{3,}:?$/.test(cell))
}

function serializeMarkdownTableRow(cells: Array<string>) {
  return `| ${cells.join(' | ')} |`
}

function splitTableCellParts(cell: string | undefined) {
  return (cell ?? '')
    .split(TABLE_BR_TOKEN)
    .map((part) => stripInlineMarkdown(part))
    .filter((part) => part.length > 0)
}

function expandTableValues(values: Array<string>, logicalRowCount: number) {
  if (values.length === logicalRowCount) return values
  if (values.length === 1) {
    return Array.from({ length: logicalRowCount }, () => values[0])
  }
  if (values.length > 1 && logicalRowCount % values.length === 0) {
    const repeatCount = logicalRowCount / values.length
    return values.flatMap((value) =>
      Array.from({ length: repeatCount }, () => value),
    )
  }
  return null
}

function inferPackedHeaderAndValues(
  parts: Array<string>,
  logicalRowCount: number,
) {
  if (parts.length <= 1) {
    return { header: parts.join(' '), values: [] }
  }

  if (parts.length > logicalRowCount) {
    const headerPartCount = parts.length - logicalRowCount
    return {
      header: parts.slice(0, headerPartCount).join(' '),
      values: parts.slice(headerPartCount),
    }
  }

  for (
    let headerPartCount = 1;
    headerPartCount <= Math.min(4, parts.length - 1);
    headerPartCount += 1
  ) {
    const valueCount = parts.length - headerPartCount
    if (valueCount >= 2 && logicalRowCount % valueCount === 0) {
      return {
        header: parts.slice(0, headerPartCount).join(' '),
        values: parts.slice(headerPartCount),
      }
    }
  }

  return { header: parts.join(' '), values: [] }
}

function normalizeVerticallyPackedTable(
  headerCells: Array<string>,
  bodyRows: Array<Array<string>>,
) {
  if (
    headerCells.length < 2 ||
    bodyRows.length < 2 ||
    !headerCells.some((cell) => cell.includes(TABLE_BR_TOKEN))
  ) {
    return null
  }

  const bodyPartsByColumn = headerCells.map((_cell, columnIndex) =>
    bodyRows.flatMap((row) => splitTableCellParts(row[columnIndex])),
  )
  const logicalRowCount = Math.max(
    0,
    ...bodyPartsByColumn.map((parts) => parts.length),
  )
  if (logicalRowCount < 2) return null

  const normalizedHeaders: Array<string> = []
  const valuesByColumn: Array<Array<string>> = []
  let populatedColumns = 0

  for (
    let columnIndex = 0;
    columnIndex < headerCells.length;
    columnIndex += 1
  ) {
    const bodyValues = bodyPartsByColumn[columnIndex]
    const headerParts = splitTableCellParts(headerCells[columnIndex])
    let header = ''
    let values: Array<string> = []

    if (bodyValues.length > 0) {
      header = headerParts.join(' ')
      values = bodyValues
    } else {
      const inferred = inferPackedHeaderAndValues(headerParts, logicalRowCount)
      header = inferred.header
      values = inferred.values
    }

    const expandedValues =
      values.length > 0
        ? expandTableValues(values, logicalRowCount)
        : Array.from({ length: logicalRowCount }, () => '')

    if (!expandedValues) return null
    if (expandedValues.some((value) => value.length > 0)) {
      populatedColumns += 1
    }

    normalizedHeaders.push(header)
    valuesByColumn.push(expandedValues)
  }

  if (populatedColumns < 2) return null

  return {
    headerCells: normalizedHeaders,
    bodyRows: Array.from({ length: logicalRowCount }, (_value, rowIndex) =>
      valuesByColumn.map((values) => values[rowIndex]),
    ),
  }
}

function normalizeInlineTableMarkdown(
  markdownContent: string,
  heading: string | null | undefined,
) {
  if (!markdownContent.trim()) return markdownContent

  const normalizedHeading = heading ? normalizeTableHeadingText(heading) : null
  const lines = markdownContent
    .replace(/<br\s*\/?>/gi, TABLE_BR_TOKEN)
    .split(/\r?\n/)
    .map((line) => line.replace(/[ \t]+/g, ' ').trimEnd())

  const firstNonEmptyIndex = lines.findIndex((line) => line.trim().length > 0)
  if (firstNonEmptyIndex >= 0) {
    const firstLine = lines[firstNonEmptyIndex].trim()
    const headingMatch = firstLine.match(/^#{2,6}\s+(.+)$/)
    if (headingMatch) {
      const bodyHeading = normalizeTableHeadingText(headingMatch[1])
      if (normalizedHeading && bodyHeading === normalizedHeading) {
        lines.splice(firstNonEmptyIndex, 1)
        if (lines[firstNonEmptyIndex]?.trim() === '') {
          lines.splice(firstNonEmptyIndex, 1)
        }
      }
    }
  }

  const tableStartIndex = lines.findIndex(
    (line, index) =>
      parseMarkdownTableRow(line) !== null &&
      index + 1 < lines.length &&
      isMarkdownTableSeparator(lines[index + 1]),
  )
  if (tableStartIndex < 0) {
    return lines.join('\n').trim()
  }

  let tableEndIndex = tableStartIndex
  while (tableEndIndex < lines.length) {
    const line = lines[tableEndIndex].trim()
    if (!line) break
    if (parseMarkdownTableRow(line) === null) break
    tableEndIndex += 1
  }

  const prefix = lines.slice(0, tableStartIndex)
  const suffix = lines.slice(tableEndIndex)
  const tableLines = lines.slice(tableStartIndex, tableEndIndex)

  const originalHeaderCells = parseMarkdownTableRow(tableLines[0])
  const separatorLine = tableLines[1]
  const originalBodyRows = tableLines
    .slice(2)
    .map((line) => parseMarkdownTableRow(line))
    .filter((row): row is Array<string> => Array.isArray(row))

  if (!separatorLine || originalBodyRows.length === 0) {
    return lines.join('\n').trim()
  }

  const headerCells = [...originalHeaderCells]
  const bodyRows = originalBodyRows.map((row) => [...row])
  const allRows = [headerCells, ...bodyRows]
  const hadLeadingEmptyColumn = allRows.every((row) => row[0] === '')
  if (hadLeadingEmptyColumn) {
    for (const row of allRows) {
      row.shift()
    }
  }

  const firstBodyRow = bodyRows[0]
  if (
    hadLeadingEmptyColumn &&
    headerCells.length === firstBodyRow.length &&
    headerCells.length >= 2 &&
    allRows.length >= 2
  ) {
    const normalizedHeaderCells = [...headerCells]
    const normalizedFirstRow = [...firstBodyRow]
    let didSplitHeader = true

    for (let index = 0; index < headerCells.length; index += 1) {
      const headerCell = stripInlineMarkdown(headerCells[index])
      const bodyCell = firstBodyRow[index]
      const parts = bodyCell
        .split(TABLE_BR_TOKEN)
        .map((part) => part.trim())
        .filter((part) => part.length > 0)

      if (index === 0) {
        if (parts.length < 2) {
          didSplitHeader = false
          break
        }
        const headerPart = parts[0].match(/^\*\*(.+?)\*\*$/)?.[1]
        if (!headerPart) {
          didSplitHeader = false
          break
        }
        normalizedHeaderCells[index] =
          `${headerCell} ${stripInlineMarkdown(headerPart)}`
            .replace(/\s+/g, ' ')
            .trim()
        normalizedFirstRow[index] = stripInlineMarkdown(
          parts.slice(1).join(' '),
        )
        continue
      }

      if (parts.length < 2) {
        didSplitHeader = false
        break
      }
      const headerPart = parts[0].match(/^\*\*(.+?)\*\*$/)?.[1]
      if (!headerPart) {
        didSplitHeader = false
        break
      }
      const valuePart = parts[parts.length - 1]
      if (!/^[0-9]+(?:\.[0-9]+)?$/.test(stripInlineMarkdown(valuePart))) {
        didSplitHeader = false
        break
      }
      const intermediateParts = parts
        .slice(1, -1)
        .map((part) => stripInlineMarkdown(part))

      normalizedHeaderCells[index] = [
        headerCell,
        stripInlineMarkdown(headerPart),
        ...intermediateParts,
      ]
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim()
      normalizedFirstRow[index] = stripInlineMarkdown(valuePart)
    }

    if (didSplitHeader) {
      headerCells.splice(0, headerCells.length, ...normalizedHeaderCells)
      bodyRows.splice(0, 1, normalizedFirstRow)
    }
  }

  const verticallyPackedTable = normalizeVerticallyPackedTable(
    headerCells,
    bodyRows,
  )
  if (verticallyPackedTable) {
    headerCells.splice(
      0,
      headerCells.length,
      ...verticallyPackedTable.headerCells,
    )
    bodyRows.splice(0, bodyRows.length, ...verticallyPackedTable.bodyRows)
  }

  const expandedBodyRows = bodyRows.flatMap((row) => {
    const splitCells = row.map((cell) =>
      cell
        .split(TABLE_BR_TOKEN)
        .map((part) => stripInlineMarkdown(part))
        .filter((part) => part.length > 0),
    )
    const valueColumnPartCounts = splitCells
      .slice(1)
      .map((parts) => parts.length)
      .filter((count) => count > 1)
    const logicalRowCount =
      valueColumnPartCounts.length > 0 ? Math.max(...valueColumnPartCounts) : 1

    if (logicalRowCount <= 1) {
      return [splitCells.map((parts) => parts.join(' '))]
    }

    const canExpand = splitCells.every(
      (parts, index) =>
        parts.length === 1 ||
        parts.length === logicalRowCount ||
        (index === 0 && parts.length % logicalRowCount === 0),
    )
    if (!canExpand) {
      return [splitCells.map((parts) => parts.join(' '))]
    }

    const firstColumnParts = splitCells[0]
    const firstColumnGroupSize =
      firstColumnParts.length % logicalRowCount === 0
        ? firstColumnParts.length / logicalRowCount
        : 1

    return Array.from({ length: logicalRowCount }, (_value, rowIndex) => {
      return splitCells.map((parts, cellIndex) => {
        if (parts.length === 1) return parts[0]
        if (cellIndex === 0 && parts.length % logicalRowCount === 0) {
          const start = rowIndex * firstColumnGroupSize
          return parts
            .slice(start, start + firstColumnGroupSize)
            .join(' ')
            .trim()
        }
        return parts[rowIndex].trim()
      })
    })
  })

  const normalizedHeaderCellsForRender = headerCells.map((cell) =>
    stripInlineMarkdown(cell.replaceAll(TABLE_BR_TOKEN, ' ')),
  )

  const normalizedTableLines = [
    serializeMarkdownTableRow(normalizedHeaderCellsForRender),
    serializeMarkdownTableRow(normalizedHeaderCellsForRender.map(() => '---')),
    ...expandedBodyRows.map((row) => serializeMarkdownTableRow(row)),
  ]

  const normalizedLines = [...prefix, ...normalizedTableLines, ...suffix]
  return normalizedLines.join('\n').trim()
}

function extractInlineTableData(markdownContent: string) {
  const lines = markdownContent
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)

  const tableStartIndex = lines.findIndex(
    (line, index) =>
      parseMarkdownTableRow(line) !== null &&
      index + 1 < lines.length &&
      isMarkdownTableSeparator(lines[index + 1]),
  )
  if (tableStartIndex < 0) return null

  const headerCells = parseMarkdownTableRow(lines[tableStartIndex])!

  const bodyRows: Array<Array<string>> = []
  for (let index = tableStartIndex + 2; index < lines.length; index += 1) {
    const row = parseMarkdownTableRow(lines[index])
    if (!row) break
    bodyRows.push(row.map((cell) => stripInlineMarkdown(cell)))
  }

  return {
    headerCells: headerCells.map((cell) => stripInlineMarkdown(cell)),
    bodyRows,
  }
}

const CodeBlock = ({ inline, className, children, ...props }: any) => {
  const [isCopied, setIsCopied] = useState(false)
  const match = /language-(\w+)/.exec(className || '')
  const rawCodeWithTrailingNewline = String(children)
  const rawCode = rawCodeWithTrailingNewline.replace(/\n$/, '')
  const isInlineCode = Boolean(
    inline ?? (!className && !rawCodeWithTrailingNewline.includes('\n')),
  )

  const handleCopy = () => {
    navigator.clipboard.writeText(rawCode)
    trackAnalyticsEvent('code_block_copied', {
      language: match ? match[1] : 'plain',
    })
    setIsCopied(true)
    setTimeout(() => setIsCopied(false), 2000)
  }

  if (isInlineCode) {
    return (
      <code
        className={cn(
          'bg-neutral-100 dark:bg-neutral-800 px-1 py-0.5 rounded text-[0.95em] font-mono text-neutral-800 dark:text-neutral-200',
          className,
        )}
        {...props}
      >
        {rawCode}
      </code>
    )
  }

  return (
    <div className="relative group my-4 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 overflow-hidden text-left">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-neutral-200 dark:border-neutral-700 bg-neutral-100/50 dark:bg-neutral-800/50">
        <span className="text-xs font-mono text-neutral-500 dark:text-neutral-400 lowercase">
          {match ? match[1] : 'code'}
        </span>
        <ActionButton
          aria-label="Copy code block"
          isQuiet
          size="S"
          title="Copy code"
          onPress={handleCopy}
        >
          {isCopied ? (
            <Check className="h-3.5 w-3.5 text-green-600" />
          ) : (
            <Copy className="h-3.5 w-3.5 text-neutral-500" />
          )}
        </ActionButton>
      </div>
      <div className="p-3 overflow-x-auto">
        <code
          className={cn(
            'text-sm font-mono text-neutral-800 dark:text-neutral-200',
            className,
          )}
          {...props}
        >
          {rawCode}
        </code>
      </div>
    </div>
  )
}

export function ChatMessage({
  messageId,
  role,
  content,
  createdAt,
  rawContent,
  onCitationClick,
  onOpenSourcePdf,
  groundingSupports,
  citations,
  visualEvidence,
  diagramRequests,
  tableRequests,
  sourceReferenceRequests,
  verificationStatus,
  onDiagramClick,
  onOpenSourceReference,
  onSourceEvidenceOpen,
  sourcesVisible = false,
  feedback,
  previousUserMessage,
  conversationUrl,
  onRetry,
}: ChatMessageProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const userMessageContentRef = useRef<HTMLDivElement>(null)
  const sourceInstructionId = useId()
  const timestamp = formatMessageTimestamp(createdAt)
  const [isUserMessageExpanded, setIsUserMessageExpanded] = useState(false)
  const [isUserMessageOverflowing, setIsUserMessageOverflowing] =
    useState(false)
  const shouldCollapseUserMessage =
    role === 'user' &&
    (content.length > USER_MESSAGE_COLLAPSE_MIN_CHARACTERS ||
      content.split('\n').length > USER_MESSAGE_COLLAPSE_MIN_LINES)

  useEffect(() => {
    if (role === 'user') {
      setIsUserMessageExpanded(false)
    }
  }, [content, role])

  useEffect(() => {
    if (role !== 'user' || !shouldCollapseUserMessage) {
      setIsUserMessageOverflowing(false)
      return
    }

    const contentElement = userMessageContentRef.current!

    const measureOverflow = () => {
      const styles = window.getComputedStyle(contentElement)
      const parsedLineHeight = Number.parseFloat(styles.lineHeight)
      const parsedFontSize = Number.parseFloat(styles.fontSize)
      const lineHeight = Number.isFinite(parsedLineHeight)
        ? parsedLineHeight
        : Number.isFinite(parsedFontSize)
          ? parsedFontSize * 1.5
          : 24
      const collapsedHeight = lineHeight * USER_MESSAGE_COLLAPSE_MIN_LINES

      setIsUserMessageOverflowing(
        contentElement.scrollHeight > collapsedHeight + 1,
      )
    }

    measureOverflow()

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(measureOverflow)
        : null

    resizeObserver?.observe(contentElement)
    window.addEventListener('resize', measureOverflow)

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', measureOverflow)
    }
  }, [content, role, shouldCollapseUserMessage])

  const openSourceEvidence = (
    chunkIndices: Array<number>,
    sourceReferenceRequest?: any | null,
  ) => {
    onSourceEvidenceOpen?.()
    trackAnalyticsEvent('source_sentence_selected', {
      chunk_count: chunkIndices.length,
      has_source_reference: Boolean(sourceReferenceRequest),
    })

    if (sourceReferenceRequest) {
      onOpenSourceReference?.(sourceReferenceRequest)
      return
    }

    if (chunkIndices.length > 0) {
      onCitationClick?.(chunkIndices)
    }
  }

  const getAvailableCitationIndices = (chunkIndices: Array<number>) => {
    if (!Array.isArray(citations) || citations.length === 0) return []
    return chunkIndices.filter(
      (chunkIndex) =>
        Number.isInteger(chunkIndex) &&
        chunkIndex >= 0 &&
        chunkIndex < citations.length,
    )
  }

  // Pre-process content to handle JSON errors and prepare for rendering
  const {
    isError,
    markdownContent,
    normalizedSupports,
    specialInsertions,
    sourceReferenceInsertions,
    blockVisualInsertions,
  } = useMemo(() => {
    if (role === 'user')
      return {
        isError: false,
        markdownContent: content,
        normalizedSupports: [] as Array<NormalizedGroundingSupport>,
        specialInsertions: [] as Array<SpecialInsertion>,
        sourceReferenceInsertions: [] as Array<SourceReferenceInsertion>,
        blockVisualInsertions: [] as Array<BlockVisualInsertion>,
      }

    if (content.startsWith('Sorry, I encountered an error: {')) {
      try {
        const jsonPart = content.replace('Sorry, I encountered an error: ', '')
        const errorObj = JSON.parse(jsonPart)
        const message =
          errorObj.error?.message ||
          errorObj.message ||
          'An unexpected error occurred.'

        if (message.includes('model is overloaded')) {
          return {
            isError: true,
            markdownContent:
              'The AI model is currently busy. Please wait a few moments and try again.',
            normalizedSupports: [] as Array<NormalizedGroundingSupport>,
            specialInsertions: [] as Array<SpecialInsertion>,
            sourceReferenceInsertions: [] as Array<SourceReferenceInsertion>,
            blockVisualInsertions: [] as Array<BlockVisualInsertion>,
          }
        }

        return {
          isError: true,
          markdownContent: message,
          normalizedSupports: [] as Array<NormalizedGroundingSupport>,
          specialInsertions: [] as Array<SpecialInsertion>,
          sourceReferenceInsertions: [] as Array<SourceReferenceInsertion>,
          blockVisualInsertions: [] as Array<BlockVisualInsertion>,
        }
      } catch {
        return {
          isError: true,
          markdownContent: content,
          normalizedSupports: [] as Array<NormalizedGroundingSupport>,
          specialInsertions: [] as Array<SpecialInsertion>,
          sourceReferenceInsertions: [] as Array<SourceReferenceInsertion>,
          blockVisualInsertions: [] as Array<BlockVisualInsertion>,
        }
      }
    }

    const hasGrounding = groundingSupports && groundingSupports.length > 0
    const sourceText =
      rawContent && rawContent.trim().length > 0 ? rawContent : content
    const { cleanedText, rawToCleanIndex } =
      stripCitationMarkersWithMap(sourceText)
    const cleanedContent = cleanedText
    const renderedContent = cleanedContent.replace(/\\n/g, '\n')
    const keyPhraseMarkers = extractKeyPhraseMarkers(sourceText)
    const diagramMarkers = extractDiagramMarkers(sourceText)
    const tableMarkers = extractTableMarkers(sourceText)
    const computedSourceReferenceInsertions = (sourceReferenceRequests ?? [])
      .map((request) =>
        locateSourceReferenceInsertion(renderedContent, request),
      )
      .filter((item): item is SourceReferenceInsertion => item !== null)
      .sort(compareInsertionOrder)
    const keyPhraseInsertions = keyPhraseMarkers.map((marker) => ({
      cleanIndex: rawToCleanIndex[marker.start],
      text: marker.text,
      chunkIndices:
        groundingSupports?.find(
          (support) =>
            marker.start >= support.segment.startIndex &&
            marker.end <= support.segment.endIndex,
        )?.groundingChunkIndices ?? [],
    }))
    const diagramRequestByOccurrence = new Map(
      (diagramRequests ?? [])
        .filter(
          (request) =>
            typeof request?.id === 'string' &&
            typeof request?.occurrenceIndex === 'number',
        )
        .map((request) => [request.occurrenceIndex, request] as const),
    )
    const tableRequestByOccurrence = new Map(
      (tableRequests ?? [])
        .filter(
          (request) =>
            typeof request?.id === 'string' &&
            typeof request?.occurrenceIndex === 'number',
        )
        .map((request) => [request.occurrenceIndex, request] as const),
    )
    const diagramInsertions = diagramMarkers
      .map((marker) => {
        const request = diagramRequestByOccurrence.get(marker.markerIndex)
        if (!request?.id) return null
        return {
          cleanIndex: rawToCleanIndex[marker.start],
          kind: 'diagram' as const,
          diagramId: request.id,
          occurrenceIndex: marker.markerIndex,
        }
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
    const tableInsertions = tableMarkers
      .map((marker) => {
        const request = tableRequestByOccurrence.get(marker.markerIndex)
        if (!request?.id) return null
        return {
          cleanIndex: rawToCleanIndex[marker.start],
          kind: 'table' as const,
          tableId: request.id,
          occurrenceIndex: marker.markerIndex,
        }
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
    const computedSpecialInsertions = [
      ...keyPhraseInsertions.map((item) => ({
        cleanIndex: item.cleanIndex,
        kind: 'keyphrase' as const,
        text: item.text,
        chunkIndices: item.chunkIndices,
      })),
    ].sort(compareInsertionOrder)

    const computedNormalizedSupports = hasGrounding
      ? normalizeGroundingSupportsForDisplay(
          sourceText,
          groundingSupports,
        ).sort((a, b) => a.segment.startIndex - b.segment.startIndex)
      : []

    return {
      isError: false,
      markdownContent: renderedContent,
      normalizedSupports: computedNormalizedSupports,
      specialInsertions: computedSpecialInsertions,
      sourceReferenceInsertions: computedSourceReferenceInsertions,
      blockVisualInsertions: [...diagramInsertions, ...tableInsertions].sort(
        compareInsertionOrder,
      ),
    }
  }, [
    content,
    rawContent,
    role,
    groundingSupports,
    diagramRequests,
    sourceReferenceRequests,
    tableRequests,
  ])

  const markdownRemarkPlugins = useMemo(() => {
    const plugins: Array<any> = [remarkGfm]
    if (
      normalizedSupports.length > 0 ||
      specialInsertions.length > 0 ||
      sourceReferenceInsertions.length > 0
    ) {
      plugins.push(
        createChatDecorationsRemarkPlugin(
          normalizedSupports,
          specialInsertions,
          sourceReferenceInsertions,
        ),
      )
    }
    if (blockVisualInsertions.length > 0) {
      plugins.push(
        createBlockVisualInsertionRemarkPlugin(blockVisualInsertions),
      )
    }
    return plugins
  }, [
    blockVisualInsertions,
    normalizedSupports,
    specialInsertions,
    sourceReferenceInsertions,
  ])
  // A durable draft or failed answer is deliberately not presented as a
  // finished answer: it has no verified grounding and must not expose copy,
  // print, or feedback actions as though it were complete.
  const shouldShowFeedbackControl = Boolean(
    messageId &&
    markdownContent &&
    verificationStatus !== 'verifying' &&
    verificationStatus !== 'unverified',
  )

  if (role === 'user') {
    return (
      <div className="flex w-full flex-col items-end animate-in fade-in slide-in-from-bottom-2 duration-300">
        <div className="max-w-[85%] rounded-2xl rounded-tr-none bg-white p-4 text-base leading-relaxed text-neutral-950 shadow-sm ring-1 ring-black/10 dark:bg-neutral-950 dark:text-white dark:ring-white/10">
          <div
            ref={userMessageContentRef}
            className={cn(
              'whitespace-pre-wrap break-words',
              shouldCollapseUserMessage &&
                isUserMessageOverflowing &&
                !isUserMessageExpanded &&
                'line-clamp-8',
            )}
          >
            {content}
          </div>
          {shouldCollapseUserMessage && isUserMessageOverflowing ? (
            <button
              type="button"
              className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-neutral-600 transition-colors hover:text-neutral-950 dark:text-white/75 dark:hover:text-white"
              aria-expanded={isUserMessageExpanded}
              onClick={() => {
                setIsUserMessageExpanded((current) => !current)
              }}
            >
              <span>{isUserMessageExpanded ? 'Show less' : 'Show more'}</span>
              {isUserMessageExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          ) : null}
        </div>
        {timestamp ? (
          <div className="mt-2 px-2 text-[10px] leading-none text-neutral-500 dark:text-neutral-400">
            {timestamp}
          </div>
        ) : null}
      </div>
    )
  }

  const diagramRequestById = new Map(
    (diagramRequests ?? [])
      .filter((request) => typeof request?.id === 'string')
      .map((request) => [request.id, request]),
  )
  const tableRequestById = new Map(
    (tableRequests ?? [])
      .filter((request) => typeof request?.id === 'string')
      .map((request) => [request.id, request]),
  )

  const getDiagramLabel = (diagramRequest: any) => {
    const base = diagramRequest?.diagramReference ?? '?'
    const subfigure =
      typeof diagramRequest?.subfigureReference === 'string' &&
      /^[a-z]$/i.test(diagramRequest.subfigureReference)
        ? diagramRequest.subfigureReference.toLowerCase()
        : null
    return subfigure ? `Diagram ${base}(${subfigure})` : `Diagram ${base}`
  }

  // Common markdown components
  const markdownComponents: any = {
    code: CodeBlock,
    strong: MarkdownStrong,
    h1: ({ children }: any) => (
      <h2 className="mt-6 mb-3 text-lg font-semibold leading-snug text-neutral-950 first:mt-0 dark:text-neutral-50 sm:text-xl">
        {children}
      </h2>
    ),
    h2: ({ children }: any) => (
      <h2 className="mt-6 mb-3 text-base font-semibold leading-snug text-neutral-950 first:mt-0 dark:text-neutral-50 sm:text-lg">
        {children}
      </h2>
    ),
    h3: ({ children }: any) => (
      <h3 className="mt-5 mb-2 text-base font-semibold leading-snug text-neutral-900 first:mt-0 dark:text-neutral-100">
        {children}
      </h3>
    ),
    h4: ({ children }: any) => (
      <h4 className="mt-4 mb-2 text-base font-semibold leading-snug text-neutral-900 first:mt-0 dark:text-neutral-100">
        {children}
      </h4>
    ),
    p: ({ children }: any) => {
      const normalizedChildren = Children.toArray(children).filter((child) => {
        return !(typeof child === 'string' && child.trim().length === 0)
      })
      const calloutChunkIndices =
        collectCitationChunkIndices(normalizedChildren)
      const availableCalloutChunkIndices =
        getAvailableCitationIndices(calloutChunkIndices)

      const isStandaloneRule =
        normalizedChildren.length === 1 &&
        isStandaloneRuleNode(normalizedChildren[0])

      if (isStandaloneRule) {
        return (
          <div className="relative my-5 rounded-xl border border-blue-200/80 bg-blue-50/70 px-4 py-3 shadow-sm dark:border-blue-900/60 dark:bg-blue-950/30">
            <div className="pr-10 text-base leading-relaxed text-blue-950 dark:text-blue-50">
              {normalizedChildren}
            </div>
            {sourcesVisible && availableCalloutChunkIndices.length > 0 ? (
              <button
                type="button"
                aria-label="See sources"
                className="absolute bottom-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-full border border-blue-300/80 bg-white/85 text-blue-800 transition-colors hover:bg-white dark:border-blue-800/80 dark:bg-blue-950/50 dark:text-blue-100 dark:hover:bg-blue-950/80"
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  openSourceEvidence(availableCalloutChunkIndices)
                }}
              >
                <BookOpen className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        )
      }

      return <p className="my-3 leading-relaxed">{children}</p>
    },
    span: ({ children, ...props }: any) => {
      const chunkIndicesStr = readDataAttribute(props, 'data-chunks')
      const sourceReferenceId = readDataAttribute(
        props,
        'data-source-reference-id',
      )
      const sourceReferenceRequest =
        typeof sourceReferenceId === 'string'
          ? (sourceReferenceRequests ?? []).find(
              (request) => request?.id === sourceReferenceId,
            )
          : null

      if (chunkIndicesStr || sourceReferenceRequest) {
        try {
          const chunkIndices =
            typeof chunkIndicesStr === 'string'
              ? JSON.parse(chunkIndicesStr)
              : []
          const safeChunkIndices = Array.isArray(chunkIndices)
            ? chunkIndices.filter(
                (chunkIndex): chunkIndex is number =>
                  typeof chunkIndex === 'number' && Number.isFinite(chunkIndex),
              )
            : []
          const availableChunkIndices =
            getAvailableCitationIndices(safeChunkIndices)

          if (
            !sourcesVisible ||
            (!sourceReferenceRequest && availableChunkIndices.length === 0)
          ) {
            return <span>{children}</span>
          }

          const tooltipLabel = sourceReferenceRequest
            ? `See ${sourceReferenceRequest.referenceText || 'source reference'} reference`
            : 'See sources'
          return (
            <span
              className={cn(
                SOURCE_SEGMENT_CLASS,
                sourceReferenceRequest ? SOURCE_REFERENCE_SEGMENT_CLASS : '',
              )}
              role="button"
              tabIndex={0}
              data-source-coach-target="true"
              aria-describedby={sourceInstructionId}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                openSourceEvidence(
                  availableChunkIndices,
                  sourceReferenceRequest,
                )
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  openSourceEvidence(
                    availableChunkIndices,
                    sourceReferenceRequest,
                  )
                }
              }}
              title={tooltipLabel}
            >
              {children}
            </span>
          )
        } catch {
          if (sourceReferenceRequest) {
            if (!sourcesVisible) {
              return <span>{children}</span>
            }

            const tooltipLabel = `See ${sourceReferenceRequest.referenceText || 'source reference'} reference`
            return (
              <span
                className={cn(
                  SOURCE_SEGMENT_CLASS,
                  SOURCE_REFERENCE_SEGMENT_CLASS,
                )}
                role="button"
                tabIndex={0}
                data-source-coach-target="true"
                aria-describedby={sourceInstructionId}
                title={tooltipLabel}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  openSourceEvidence([], sourceReferenceRequest)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    openSourceEvidence([], sourceReferenceRequest)
                  }
                }}
              >
                {children}
              </span>
            )
          }
        }
      }
      return <span {...props}>{children}</span>
    },
    'diagram-inline': ({ ...props }: any) => {
      const diagramId = readDataAttribute(props, 'data-diagram-id')
      const diagramRequest =
        typeof diagramId === 'string' ? diagramRequestById.get(diagramId) : null

      if (!diagramRequest) {
        return null
      }

      if (diagramRequest.status === 'ready' && diagramRequest.inlineImageUrl) {
        return (
          <span className="mx-auto my-4 flex w-full justify-center">
            <button
              type="button"
              className="overflow-hidden rounded-xl border border-neutral-200 bg-neutral-50 shadow-sm transition-transform hover:scale-[1.01] dark:border-neutral-700 dark:bg-neutral-900"
              onClick={() => {
                trackAnalyticsEvent('diagram_opened', {
                  has_source_pdf: Boolean(diagramRequest.sourcePdfUrl),
                  has_page: Boolean(diagramRequest.pageNumber),
                })
                if (diagramRequest.sourcePdfUrl) {
                  trackAnalyticsEvent('source_document_opened', {
                    source_context: 'diagram',
                    has_page: Boolean(diagramRequest.pageNumber),
                  })
                  onOpenSourcePdf?.({
                    title:
                      diagramRequest.documentTitle ||
                      getDiagramLabel(diagramRequest),
                    pageStart: diagramRequest.pageNumber ?? undefined,
                    pageEnd: diagramRequest.pageNumber ?? undefined,
                    sourcePdfUrl: diagramRequest.sourcePdfUrl,
                  })
                  return
                }
                onDiagramClick?.(diagramRequest)
              }}
            >
              <img
                src={diagramRequest.inlineImageUrl}
                alt={`${getDiagramLabel(diagramRequest)} on page ${diagramRequest.pageNumber}`}
                className="block h-auto max-w-[500px] object-contain"
              />
            </button>
          </span>
        )
      }

      if (diagramRequest.status === 'failed') {
        return (
          <span className="mx-1 inline-flex align-middle rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
            {diagramRequest.pageNumber
              ? `${getDiagramLabel(diagramRequest)} unavailable for page ${diagramRequest.pageNumber}`
              : 'Diagram unavailable'}
          </span>
        )
      }

      return (
        <span className="mx-1 inline-flex min-w-[220px] items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-2.5 py-1.5 text-xs text-neutral-600 align-middle dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300">
          <span className="flex gap-1">
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400 [animation-delay:-0.3s]"></span>
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400 [animation-delay:-0.15s]"></span>
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400"></span>
          </span>
          <span>
            Retrieving {getDiagramLabel(diagramRequest).toLowerCase()} from page{' '}
            {diagramRequest.pageNumber}
            {diagramRequest.documentTitle
              ? ` in ${diagramRequest.documentTitle}`
              : ''}
          </span>
        </span>
      )
    },
    'keyphrase-card': ({ ...props }: any) => {
      const encodedText = readDataAttribute(props, 'data-keyphrase')
      const encodedChunks = readDataAttribute(props, 'data-chunks')
      const text =
        typeof encodedText === 'string' ? decodeURIComponent(encodedText) : ''
      let chunkIndices: Array<number> = []

      if (typeof encodedChunks === 'string') {
        try {
          const parsed = JSON.parse(decodeURIComponent(encodedChunks))
          if (Array.isArray(parsed)) {
            chunkIndices = parsed.filter(
              (value): value is number =>
                typeof value === 'number' && Number.isFinite(value),
            )
          }
        } catch {
          chunkIndices = []
        }
      }

      return (
        <span className="my-5 block rounded-xl border border-blue-200/80 bg-blue-50/70 px-4 py-3 shadow-sm dark:border-blue-900/60 dark:bg-blue-950/30">
          <span className="relative block pr-10 text-base font-semibold leading-relaxed text-blue-950 dark:text-blue-50">
            {text}
            {sourcesVisible &&
            getAvailableCitationIndices(chunkIndices).length > 0 ? (
              <button
                type="button"
                aria-label="See sources"
                className="absolute bottom-0 right-0 inline-flex h-8 w-8 items-center justify-center rounded-full border border-blue-300/80 bg-white/85 text-blue-800 transition-colors hover:bg-white dark:border-blue-800/80 dark:bg-blue-950/50 dark:text-blue-100 dark:hover:bg-blue-950/80"
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  openSourceEvidence(getAvailableCitationIndices(chunkIndices))
                }}
              >
                <BookOpen className="h-4 w-4" />
              </button>
            ) : null}
          </span>
        </span>
      )
    },
    'table-inline': ({ ...props }: any) => {
      const tableId = readDataAttribute(props, 'data-table-id')
      const tableRequest =
        typeof tableId === 'string' ? tableRequestById.get(tableId) : null

      if (!tableRequest) return null

      if (
        tableRequest.status === 'ready' &&
        typeof tableRequest.markdownContent === 'string' &&
        tableRequest.markdownContent.trim().length > 0
      ) {
        const normalizedTableMarkdown = normalizeInlineTableMarkdown(
          tableRequest.markdownContent,
          tableRequest.heading,
        )
        const normalizedTableData = extractInlineTableData(
          normalizedTableMarkdown,
        )
        const exceedsColumnLimit =
          (normalizedTableData?.headerCells.length ?? 0) >
          INLINE_TABLE_PREVIEW_MAX_COLUMNS
        const exceedsRowLimit =
          (normalizedTableData?.bodyRows.length ?? 0) >
          INLINE_TABLE_PREVIEW_MAX_ROWS
        const shouldRenderPreview =
          !!normalizedTableData && (exceedsColumnLimit || exceedsRowLimit)

        const openSourceDocument = () => {
          trackAnalyticsEvent('source_document_opened', {
            source_context: 'table',
            has_page: Boolean(tableRequest.pageNumber),
          })
          onOpenSourcePdf?.({
            title:
              tableRequest.documentTitle ||
              tableRequest.heading ||
              `Table ${tableRequest.tableReference}`,
            pageStart: tableRequest.pageNumber ?? undefined,
            pageEnd: tableRequest.pageNumber ?? undefined,
            pageImageUrl: tableRequest.pageImageUrl,
            sourcePdfUrl: tableRequest.sourcePdfUrl,
          })
        }

        return (
          <div className="my-5">
            <div className="mb-2 text-xs font-medium uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400">
              {tableRequest.heading || `Table ${tableRequest.tableReference}`}
            </div>
            <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
              <span>
                {tableRequest.documentTitle || 'Source document'}
                {tableRequest.pageNumber
                  ? ` · Page ${tableRequest.pageNumber}`
                  : ''}
              </span>
              {tableRequest.sourcePdfUrl ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 font-medium text-neutral-500 transition-colors hover:text-neutral-700 hover:underline dark:text-neutral-400 dark:hover:text-neutral-200"
                  onClick={openSourceDocument}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  <span>Open document</span>
                </button>
              ) : null}
            </div>
            {shouldRenderPreview ? (
              <div className="my-6">
                <div className="relative w-full overflow-hidden rounded-lg border border-neutral-200 dark:border-neutral-800">
                  <table className="w-full border-collapse text-sm">
                    <thead className="bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-800">
                      <tr className="transition-colors">
                        {normalizedTableData.headerCells
                          .slice(0, INLINE_TABLE_PREVIEW_MAX_COLUMNS)
                          .map((cell, index, cells) => (
                            <th
                              key={`${cell}-${index}`}
                              className={cn(
                                'px-4 py-3 text-left font-bold text-neutral-900 dark:text-neutral-100 first:pl-6 last:pr-4',
                                exceedsColumnLimit && index === cells.length - 1
                                  ? 'bg-neutral-100/70 dark:bg-neutral-900/70'
                                  : '',
                              )}
                            >
                              {cell}
                            </th>
                          ))}
                      </tr>
                    </thead>
                    <tbody>
                      {normalizedTableData.bodyRows
                        .slice(0, INLINE_TABLE_PREVIEW_MAX_ROWS)
                        .map((row, rowIndex, rows) => (
                          <tr
                            key={`${row.join('|')}-${rowIndex}`}
                            className="transition-colors"
                          >
                            {row
                              .slice(0, INLINE_TABLE_PREVIEW_MAX_COLUMNS)
                              .map((cell, cellIndex, cells) => (
                                <td
                                  key={`${cell}-${cellIndex}`}
                                  className={cn(
                                    'px-4 py-3 text-neutral-700 dark:text-neutral-300 border-t border-neutral-100 dark:border-neutral-800/50 first:pl-6 last:pr-4',
                                    exceedsColumnLimit &&
                                      cellIndex === cells.length - 1
                                      ? 'bg-neutral-50/70 dark:bg-neutral-900/50'
                                      : '',
                                    exceedsRowLimit &&
                                      rowIndex === rows.length - 1
                                      ? 'bg-neutral-50/70 dark:bg-neutral-900/50'
                                      : '',
                                  )}
                                >
                                  {cell}
                                </td>
                              ))}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  {exceedsColumnLimit ? (
                    <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white via-white/90 to-transparent dark:from-neutral-950 dark:via-neutral-950/90" />
                  ) : null}
                  {exceedsRowLimit ? (
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white via-white/90 to-transparent dark:from-neutral-950 dark:via-neutral-950/90" />
                  ) : null}
                  {tableRequest.sourcePdfUrl ? (
                    <button
                      type="button"
                      className="absolute bottom-3 right-3 z-10 rounded-full border border-neutral-300 bg-white/95 px-3 py-1.5 text-xs font-semibold text-neutral-800 shadow-sm transition-colors hover:bg-white dark:border-neutral-700 dark:bg-neutral-950/95 dark:text-neutral-100 dark:hover:bg-neutral-950"
                      onClick={openSourceDocument}
                    >
                      View full table
                    </button>
                  ) : null}
                </div>
              </div>
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  table: markdownComponents.table,
                  thead: markdownComponents.thead,
                  th: markdownComponents.th,
                  td: markdownComponents.td,
                  tr: markdownComponents.tr,
                  p: ({ children }: any) => <>{children}</>,
                }}
              >
                {normalizedTableMarkdown}
              </ReactMarkdown>
            )}
          </div>
        )
      }

      if (tableRequest.status === 'failed') {
        return (
          <div className="my-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
            {`Table ${tableRequest.tableReference} unavailable`}
          </div>
        )
      }

      return (
        <div className="my-4 inline-flex min-w-[220px] items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-600 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300">
          <span className="flex gap-1">
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400 [animation-delay:-0.3s]"></span>
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400 [animation-delay:-0.15s]"></span>
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400"></span>
          </span>
          <span>{`Loading Table ${tableRequest.tableReference}...`}</span>
        </div>
      )
    },
    ul: ({ children }: any) => (
      <ul className="my-5 list-disc space-y-2 pl-6 marker:text-neutral-400 dark:marker:text-neutral-500">
        {children}
      </ul>
    ),
    ol: ({ children }: any) => (
      <ol className="my-5 list-decimal space-y-2 pl-6 marker:font-semibold marker:text-neutral-500 dark:marker:text-neutral-400">
        {children}
      </ol>
    ),
    li: ({ children }: any) => (
      <li className="pl-1 leading-relaxed text-neutral-800 dark:text-neutral-200">
        {children}
      </li>
    ),
    table: ({ children }: any) => (
      <div className="my-6 w-full overflow-x-auto rounded-lg border border-neutral-200 dark:border-neutral-800">
        <table className="w-full border-collapse text-sm">{children}</table>
      </div>
    ),
    thead: ({ children }: any) => (
      <thead className="bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-800">
        {children}
      </thead>
    ),
    th: ({ children }: any) => (
      <th className="px-4 py-3 text-left font-bold text-neutral-900 dark:text-neutral-100 first:pl-6 last:pr-4">
        {children}
      </th>
    ),
    td: ({ children }: any) => (
      <td className="px-4 py-3 text-neutral-700 dark:text-neutral-300 border-t border-neutral-100 dark:border-neutral-800/50 first:pl-6 last:pr-4">
        {children}
      </td>
    ),
    tr: ({ children }: any) => (
      <tr className="hover:bg-neutral-50/50 dark:hover:bg-neutral-900/20 transition-colors">
        {children}
      </tr>
    ),
    a: ({ href, children }: any) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
      >
        {children}
      </a>
    ),
  }

  return (
    <div className="flex w-full flex-col items-start animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div
        ref={containerRef}
        className={cn(
          'relative w-full px-0 py-1 text-neutral-900 dark:text-neutral-50',
          isError && 'border-l-2 border-red-300 pl-4 dark:border-red-800',
        )}
      >
        {sourcesVisible ? (
          <span id={sourceInstructionId} className="sr-only">
            Press Enter or Space to see sources for this text.
          </span>
        ) : null}
        <div className="space-y-2 text-base prose dark:prose-invert prose-neutral max-w-none leading-relaxed text-left">
          {verificationStatus && verificationStatus !== 'verified' ? (
            <div className="mb-2 flex items-center gap-2 text-[11px] font-medium tracking-[0.02em] text-neutral-500 dark:text-neutral-400">
              <span
                className={cn(
                  'inline-block h-1.5 w-1.5 rounded-full',
                  verificationStatus === 'unverified' &&
                    'bg-amber-500 dark:bg-amber-400',
                  verificationStatus === 'verifying' &&
                    'bg-sky-500 dark:bg-sky-400',
                )}
              />
              <span>
                {verificationStatus === 'unverified'
                  ? 'Unverified'
                  : 'Verifying'}
              </span>
              {verificationStatus === 'unverified' && onRetry ? (
                <ActionButton
                  aria-label="Retry answer"
                  onPress={onRetry}
                  size="S"
                >
                  <RotateCcw />
                </ActionButton>
              ) : null}
            </div>
          ) : null}

          {isError && (
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400 font-semibold mb-2">
              <AlertCircle size={16} />
              <span>System note</span>
            </div>
          )}

          {!markdownContent ? (
            <div className="flex items-center gap-2 text-neutral-400 italic py-1">
              <span className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-neutral-300 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                <span className="w-1.5 h-1.5 bg-neutral-300 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                <span className="w-1.5 h-1.5 bg-neutral-300 rounded-full animate-bounce"></span>
              </span>
            </div>
          ) : (
            <ReactMarkdown
              remarkPlugins={markdownRemarkPlugins}
              rehypePlugins={[
                rehypeRaw,
                [rehypeSanitize, markdownSanitizeSchema],
              ]}
              components={markdownComponents}
            >
              {markdownContent}
            </ReactMarkdown>
          )}
        </div>
      </div>
      {timestamp ? (
        <div className="mt-3 w-full px-1 text-left text-[10px] leading-none text-neutral-500 dark:text-neutral-400">
          {timestamp}
        </div>
      ) : null}
      {shouldShowFeedbackControl ? (
        <MessageFeedbackControl
          messageId={messageId as string}
          markdownContent={markdownContent}
          previousUserMessage={previousUserMessage ?? null}
          conversationUrl={conversationUrl ?? null}
          timestampLabel={timestamp}
          initialFeedback={feedback ?? null}
        />
      ) : null}
    </div>
  )
}

export const __chatMessageTestUtils = {
  appendBlockVisualsToListItem,
  buildSourceReferencePattern,
  collectCitationChunkIndices,
  compareInsertionOrder,
  MarkdownStrong,
  decorateBlockVisualInsertions,
  decorateMarkdownTree,
  escapeHtml,
  expandTableValues,
  extractInlineTableData,
  findActiveSourceReference,
  findActiveSupport,
  formatMessageTimestamp,
  getNodeOffset,
  inferPackedHeaderAndValues,
  isMarkdownTableSeparator,
  isStandaloneRuleNode,
  locateSourceReferenceInsertion,
  nodeContainsCleanIndex,
  normalizeInlineTableMarkdown,
  normalizeTableHeadingText,
  normalizeVerticallyPackedTable,
  parseMarkdownTableRow,
  readDataAttribute,
  serializeMarkdownTableRow,
  splitTableCellParts,
  splitTextNodeWithDecorations,
  stripInlineMarkdown,
}
